import { useState } from 'react'
import { ScrollView, View } from 'react-native'
import { colors } from '@ca/ui/colors'
import ChipButton from '@ca/ui/elements/ChipButton'
import type { MeasurementUiMessage, MeasurementUiOvalState } from '../types'
import type { MeasurementFlowDebugScenario } from './debugScenarios'

type DebugViewState = {
  message: MeasurementUiMessage | null
  ovalState: MeasurementUiOvalState
}

type Props<ViewState extends DebugViewState> = {
  scenarios: MeasurementFlowDebugScenario<ViewState>[]
  activeKey: string | null
  onSelect: (scenario: MeasurementFlowDebugScenario<ViewState> | null) => void
}

/**
 * Dev-only floating panel that forces the flow UI into any measurement state.
 * Presentation-only: the live capture pipeline keeps running underneath, and
 * selecting "Live" returns to whatever state it is currently in.
 * Each chip also shows the mapped message consumed by `@ui` Messages.
 */
export function MeasurementFlowDebugControls<ViewState extends DebugViewState>({
  scenarios,
  activeKey,
  onSelect,
}: Props<ViewState>) {
  const [isOpen, setIsOpen] = useState(false)
  const hasOverride = activeKey != null
  const activeScenario = scenarios.find(
    (scenario) => scenario.key === activeKey
  )

  return (
    <View className="absolute right-3 top-3" pointerEvents="box-none">
      <View className="items-end gap-2">
        <ChipButton
          label={activeScenario ? 'DEV' : undefined}
          value={activeScenario?.label ?? 'DEV'}
          color={hasOverride ? colors.amber[300] : undefined}
          onPress={() => setIsOpen((open) => !open)}
        />
        {isOpen && (
          <View
            className="overflow-hidden rounded-xl bg-black/80 p-3"
            style={{ maxHeight: 420 }}
          >
            <ScrollView contentContainerClassName="items-end gap-2">
              <ChipButton
                value="Live"
                color={!hasOverride ? colors.amber[300] : undefined}
                onPress={() => onSelect(null)}
              />
              {scenarios.map((scenario) => (
                <ChipButton
                  fullWidth
                  key={scenario.key}
                  label={scenario.viewState.message ?? undefined}
                  value={scenario.label}
                  color={
                    scenario.key === activeKey ? colors.amber[300] : undefined
                  }
                  onPress={() => onSelect(scenario)}
                />
              ))}
            </ScrollView>
          </View>
        )}
      </View>
    </View>
  )
}
